// useApi.js
import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/apiConfig';
import Cookies from 'universal-cookie';
import dialogEventEmitter from '../utils/dialogEventEmitter';
import { FEATURE_IDS, isFeatureEnabled } from '../utils/supportedFeatures';
import { fetchClientsFromNewAppcom } from '../api/connectedClientsNewAppcom';

// Utility function to make API calls based on the provided endpoint
// excludeHeaders examples:
// - ['apiKey', 'token'] - excludes multiple headers
// - ['apiKey'] - excludes single header in array
// - 'apiKey' - excludes single header as string
// - [] - excludes nothing (default)
const useApi = async (endpointName, params = {}, payload = {}, possibleHeaders = [], excludeHeaders = []) => {
  try {
    const cookies = new Cookies();
    const endpoint = API_ENDPOINTS[endpointName];
    if (!endpoint) {
      throw new Error('Endpoint not found');
    }

    // PRJCBUGEN-66106: the device-level clients list moves to the new appcom behind a flag.
    if (endpointName === 'GET_CLIENT_LIST_DEVICE_LEVEL' && isFeatureEnabled(FEATURE_IDS.APPCOM_DEVICE_APIS_MIGRATION_ENABLED_WEB)) {
      return await fetchClientsFromNewAppcom(useApi, params, payload, excludeHeaders);
    }

    // Replace dynamic values (e.g., {id}) in the URL
    let url = endpoint.url;
    // Replace common cookie-based parameters automatically
    const insightUserId = cookies.get('_Id') || cookies.get('userid') || cookies.get('userId');
    url = url.replace('{userId}', insightUserId);
    url = url.replace('{_Id}', cookies.get('_Id'));
    url = url.replace('{accountId}', cookies.get('accountId'));

    // Replace parameters passed from the calling code
    for (const param in params) {
      if (param === 'groupId' && window.location.pathname.includes('device-group')) {
        // Special handling for groupId - append as /group/{groupId} only when URL contains 'device-group'
        url = url.replace(/\{groupId\}/g, `/group/${localStorage.getItem('currentDeviceGrpId')}`);
      } else if (param != 'groupId') {
        url = url.replace(`{${param}}`, params[param]);
      }
    }
    // Remove optional placeholders that were not provided (currently supports groupId, stackUuidParam)
    url = url.replace(/\/\{groupId\}/g, '');
    url = url.replace(/\{groupId\}/g, '');
    url = url.replace(/\{stackUuidParam\}/g, '');
    // Collapse duplicate slashes that can arise after removing optional segments
    url = url.replace(/([^:]\/)\/+/g, '$1');

    // Remove unused optional placeholders
    url = url.replace(/\/\{groupId\}/g, '');
    url = url.replace(/\{groupId\}/g, '');
    // Collapse duplicate slashes that can arise after removing optional segments
    url = url.replace(/([^:]\/)\/+/g, '$1');

    // Prepare request body or params based on the method
    const config = {
      method: endpoint.method,
      url,
      excludeHeaders // Pass the excludeHeaders array to axiosInstance
    };

    // Per-endpoint timeout override. axiosInstance sets a 60s default, which a few endpoints
    // legitimately exceed — a stack delete runs one full device-delete cascade PER MEMBER, so a
    // 3-unit stack measured 40-60s in maint-dev and sat right on the default. Timing out there is
    // the worst outcome available: the server completes the delete, the client reports failure.
    // Number.isFinite, not truthiness (Copilot review on #2748): axios reads `timeout: 0` as "no
    // timeout at all", which a truthiness guard would silently drop, and `true` would otherwise sail
    // through as a 1ms timeout that fails every request on that endpoint. A defined-but-non-numeric
    // value is ignored rather than coerced, so the axiosInstance default still applies — and the
    // apiConfig test below fails the build if any endpoint declares a timeout that is not a number,
    // so a misconfiguration cannot reach runtime to be silently ignored.
    if (Number.isFinite(endpoint.timeout)) {
      config.timeout = endpoint.timeout;
    }

    if (endpoint.method === 'post' || endpoint.method === 'put' || endpoint.method === 'delete') {
      config.data = payload.data !== undefined ? payload.data : {}; // For POST/PUT/DELETE send body; default to {}
    }

    if (endpoint.method === 'get') {
      config.params = payload.data || {}; // For GET and DELETE, send as query params
    }
    // Support custom responseType (e.g., 'blob' for CSV download)
    if (payload && payload.responseType) {
      config.responseType = payload.responseType;
    }
    // Allow per-call custom headers without affecting others
    if (payload && payload.headers) {
      config.headers = { ...(config.headers || {}), ...payload.headers };
    }
    config.possibleHeaders = possibleHeaders;
    const response = await axiosInstance(config);

    if (response.data?.response?.responseCode == '8056') {
      dialogEventEmitter.emit('showDialog', {
        title: 'sessionTimeoutTitle',
        content: 'sessionTimeoutContent',
        calledFrom: 'useApi'
      });
      return;
    }

    // Handle 44030 (support session expired) - return undefined so callers don't process expired payload
    const responseCode = response.data?.response?.responseCode;
    if (responseCode == '44030' || responseCode === 44030) {
      return;
    }

    return response.data;
  } catch (error) {
    // Handle support session expired rejection from interceptor
    if (error?.isSupportSessionExpired) {
      return;
    }
    throw error; // Rethrow or handle errors as needed
  }
};

export default useApi;
